import { useState, useEffect, useCallback, useMemo } from 'react';
import { NoteItem } from '../../types/notes';
import { notesApi } from '../../api/notesApi';

interface UseNotesProps {
    userId?: string;
}

interface UseNotesReturn {
    notes: { [key: string]: NoteItem };
    notesList: NoteItem[];
    loading: boolean;
    error: string | null;
    submitting: boolean;
    addNote: (text: string) => Promise<void>;
    updateNote: (noteKey: string, currentNote: NoteItem, newText: string) => Promise<void>;
    deleteNote: (noteKey: string) => Promise<void>;
    clearError: () => void;
}

export const useNotes = ({ userId }: UseNotesProps): UseNotesReturn => {
    const [notes, setNotes] = useState<{ [key: string]: NoteItem }>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    // Convert notes object to sorted array (newest first)
    const notesList = useMemo(() =>
        Object.values(notes).sort((a, b) =>
            b.createdAt.localeCompare(a.createdAt)
        ),
        [notes]
    );

    // Load notes on mount or when userId changes
    useEffect(() => {
        let isMounted = true;

        const load = async () => {
            if (!userId) {
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const loadedNotes = await notesApi.getNotes(userId);
                if (isMounted) {
                    setNotes(loadedNotes);
                }
            } catch (err) {
                if (isMounted) {
                    console.error('Error loading notes:', err);
                    setError('Kunde inte ladda anteckningar');
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            isMounted = false;
        };
    }, [userId]);

    // Reload notes helper (for use after mutations)
    const reloadNotes = useCallback(async () => {
        if (!userId) return;

        try {
            const loadedNotes = await notesApi.getNotes(userId);
            setNotes(loadedNotes);
        } catch (err) {
            console.error('Error reloading notes:', err);
        }
    }, [userId]);

    // Add new note
    const addNote = useCallback(async (text: string) => {
        if (!userId) {
            setError('Kontextfel: ingen användare vald');
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            await notesApi.createNote(userId, text);
            await reloadNotes();
        } catch (err: any) {
            console.error('Error adding note:', err);
            setError(err.message || 'Kunde inte lägga till anteckning');
            throw err;
        } finally {
            setSubmitting(false);
        }
    }, [userId, reloadNotes]);

    // Update note text
    const updateNote = useCallback(async (noteKey: string, currentNote: NoteItem, newText: string) => {
        if (!userId) return;

        setSubmitting(true);
        setError(null);

        try {
            await notesApi.updateNote(userId, noteKey, currentNote, newText);
            await reloadNotes();
        } catch (err: any) {
            console.error('Error updating note:', err);
            setError(err.message || 'Kunde inte uppdatera anteckning');
            throw err;
        } finally {
            setSubmitting(false);
        }
    }, [userId, reloadNotes]);

    // Delete note
    const deleteNote = useCallback(async (noteKey: string) => {
        if (!userId) return;

        try {
            await notesApi.deleteNote(userId, noteKey);
            await reloadNotes();
        } catch (err) {
            console.error('Error deleting note:', err);
            setError('Kunde inte radera anteckning');
        }
    }, [userId, reloadNotes]);

    // Clear error
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    return {
        notes,
        notesList,
        loading,
        error,
        submitting,
        addNote,
        updateNote,
        deleteNote,
        clearError,
    };
};
